import { Injectable } from "@angular/core";
import { select, Store } from "@ngrx/store";
import {
	AbstractTranslatableAlertService,
	AuthSelector,
	CORE_ROUTE,
	CoreState,
	CustomerManagementSelector,
	DisallowedActionCodes,
	Jurisdiction,
	JurisdictionSelector,
} from "@obg/ngx.core";
import { TranslationGroup } from "@obg/ngx.core.content";
import { RouteAction, RouteSelector } from "@obg/ngx.router";
import { ignoreNil } from "@obg/rxjs";
import { combineLatest, Observable } from "rxjs";
import { filter, map, pairwise, switchMap, take } from "rxjs/operators";

import { GAMES_OF_CHANCE_ROUTES, HAMPI_ROUTE_NOT_ALLOWED_ALERT_ID, SPORTSBOOK_ROUTES } from "./hampi.const";

@Injectable({
	providedIn: "root",
})
export class HampiService {
	private afterLogin$ = this.store.select(AuthSelector.isAuthenticated).pipe(
		pairwise(),
		filter(([prev, next]) => !prev && next)
	);

	constructor(
		private store: Store<CoreState>,
		private alertService: AbstractTranslatableAlertService
	) {}

	initialize$() {
		return this.afterLogin$.pipe(
			switchMap(() => this.store.pipe(select(JurisdictionSelector.get), ignoreNil())),
			filter((jurisdiction) => jurisdiction === Jurisdiction.Mta),
			switchMap(() => this.store.select(RouteSelector.getCurrent)),
			map((currentRoute) => currentRoute?.name),
			switchMap((route) => this.isProductAllowed$(route, true))
		);
	}

	isProductAllowed$(routeName?: string, redirect?: boolean, preserveOnLogout?: boolean): Observable<boolean> {
		return combineLatest([
			this.store.select(CustomerManagementSelector.getCustomer).pipe(
				ignoreNil(),
				map((customer) => customer.allowedActions)
			),
			this.store.select(RouteSelector.getCurrent),
		]).pipe(
			take(1),
			map(([allowedActions, currentRoute]) => {
				const gamesOfChanceBlocked =
					allowedActions.betCasino != null &&
					!allowedActions.betCasino.isAllowed &&
					allowedActions.betCasino.disallowedReasons.includes(
						DisallowedActionCodes.M_CURRENTCUSTOMER_PRODUCTBLOCKED
					);
				const sportsBookBlocked =
					allowedActions.betSportsbook != null &&
					!allowedActions.betSportsbook.isAllowed &&
					allowedActions.betSportsbook.disallowedReasons.includes(
						DisallowedActionCodes.M_CURRENTCUSTOMER_PRODUCTBLOCKED
					);

				if (
					(this.isGameOfChanceRoute(routeName) && gamesOfChanceBlocked) ||
					(this.isSportsbookRoute(routeName) && sportsBookBlocked)
				) {
					const isCurrentRouteSportsbookAndBlocked =
						this.isSportsbookRoute(currentRoute?.name) && sportsBookBlocked;
					const isCurrentRouteGameOfChanceAndBlocked =
						this.isGameOfChanceRoute(currentRoute?.name) && gamesOfChanceBlocked;
					const isLoginPage = currentRoute?.name === CORE_ROUTE.auth.login;

					const shouldRedirect =
						!currentRoute ||
						isLoginPage ||
						isCurrentRouteSportsbookAndBlocked ||
						isCurrentRouteGameOfChanceAndBlocked;

					if (redirect && shouldRedirect) {
						this.store.dispatch(RouteAction.navigate(CORE_ROUTE.home));
					}

					this.openAlert(preserveOnLogout);
					return false;
				}

				return true;
			})
		);
	}

	openAlert(preserveOnLogout?: boolean) {
		this.alertService.open(
			{
				id: HAMPI_ROUTE_NOT_ALLOWED_ALERT_ID,
				title: "mta.hampi.confirmation-title",
				message: "mta.hampi.confirmation-caption",
				hideClose: true,
				dialogConfig: {
					disableClose: true,
				},
				buttons: [
					{
						text: "general.ok",
					},
				],
				preserveOnLogout,
			},
			[TranslationGroup.Common]
		);
	}

	private isGameOfChanceRoute(routeName?: string): boolean {
		return (
			!!routeName && GAMES_OF_CHANCE_ROUTES.some((route) => routeName.toLowerCase().includes(route.toLowerCase()))
		);
	}

	private isSportsbookRoute(routeName?: string): boolean {
		return !!routeName && SPORTSBOOK_ROUTES.some((route) => routeName.toLowerCase().includes(route.toLowerCase()));
	}
}
